import { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import { ajustarOrigen, normalizarTexto, pareceDireccionExacta, type SectorGeo } from '@rumbo/form'
import { obtener } from '@/servicios/api'
import { Entrada, Espera, MensajeAyuda } from '@/componentes/ui'

// maplibre-gl pesa ~280 KB comprimido: la mayoría de quienes responden usan
// "Elegir de la lista" (la pestaña por defecto), así que el mapa se carga
// solo si alguien realmente abre esa pestaña — el bundle público no lo paga.
const MapaSelector = lazy(() => import('./MapaSelector').then((m) => ({ default: m.MapaSelector })))

interface SectorApi {
  id: string
  cod: string
  nombre: string
  tipo: string
  lat: number
  lng: number
  geom: { type: 'MultiPolygon'; coordinates: number[][][][] } | null
}

export interface ValorOrigen {
  metodo: 'mapa' | 'lista' | 'texto'
  sectorId?: string
  sectorTexto?: string
  celda?: string
  punto?: { lat: number; lng: number }
}

const aSectorGeo = (s: SectorApi): SectorGeo => ({
  id: s.id,
  nombre: s.nombre,
  centroide: { lat: s.lat, lng: s.lng },
  poligonos: (s.geom?.coordinates ?? []) as unknown as SectorGeo['poligonos'],
})

/**
 * Selector de sector de origen. El ajuste de privacidad (D-008) corre
 * SIEMPRE en este componente, en el dispositivo: lo único que sale de aquí
 * hacia el resto del formulario es un `ValorOrigen` ya ajustado (sector o
 * celda de ~500 m) — nunca la coordenada cruda del mapa.
 */
export function SelectorOrigen({
  codParroquia,
  valor,
  onCambio,
}: {
  codParroquia: string | null
  valor: ValorOrigen | undefined
  onCambio: (v: ValorOrigen) => void
}) {
  const [modo, setModo] = useState<'lista' | 'mapa' | 'texto'>('lista')
  const [busqueda, setBusqueda] = useState('')
  const [sectores, setSectores] = useState<SectorApi[]>([])
  const [cargando, setCargando] = useState(false)
  const [textoLibre, setTextoLibre] = useState(valor?.sectorTexto ?? '')

  useEffect(() => {
    if (!codParroquia) return
    setCargando(true)
    obtener<SectorApi[]>(`/catalogos/parroquias/${codParroquia}/sectores`, { geom: '1' })
      .then(setSectores)
      .finally(() => setCargando(false))
  }, [codParroquia])

  const filtrados = useMemo(() => {
    if (!busqueda.trim()) return sectores.slice(0, 40)
    const t = normalizarTexto(busqueda)
    return sectores.filter((s) => normalizarTexto(s.nombre).includes(t)).slice(0, 40)
  }, [sectores, busqueda])

  if (!codParroquia) {
    return <MensajeAyuda>Seleccione primero el estado, municipio y parroquia de origen.</MensajeAyuda>
  }

  const sectorElegido = sectores.find((s) => s.id === valor?.sectorId)
  const avisoDireccion = modo === 'texto' && pareceDireccionExacta(textoLibre)

  return (
    <div>
      <div role="tablist" className="mb-3 flex gap-2" aria-label="Cómo indicar su sector">
        {(['lista', 'mapa', 'texto'] as const).map((m) => (
          <button
            key={m}
            type="button"
            role="tab"
            aria-selected={modo === m}
            onClick={() => setModo(m)}
            className={`objetivo-tactil foco-visible rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              modo === m ? 'bg-azul text-white' : 'bg-superficie-alt text-texto-suave hover:bg-borde'
            }`}
          >
            {m === 'lista' ? 'Elegir de la lista' : m === 'mapa' ? 'Marcar en el mapa' : 'Escribir el nombre'}
          </button>
        ))}
      </div>

      {modo === 'lista' && (
        <div>
          <Entrada
            type="search"
            placeholder="Busque su urbanización, barrio o sector…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            aria-label="Buscar sector"
          />
          {cargando ? (
            <Espera etiqueta="Cargando sectores…" />
          ) : (
            <ul className="mt-2 max-h-64 overflow-y-auto rounded-[var(--radius-rumbo)] border border-borde">
              {filtrados.length === 0 && <li className="p-3 text-sm text-texto-suave">No se encontraron sectores. Pruebe con «Escribir el nombre».</li>}
              {filtrados.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => onCambio({ metodo: 'lista', sectorId: s.id })}
                    className={`objetivo-tactil foco-visible flex w-full items-center justify-between px-4 py-2.5 text-left text-sm hover:bg-superficie-alt ${
                      valor?.sectorId === s.id ? 'bg-azul-suave font-medium' : ''
                    }`}
                  >
                    <span>{s.nombre}</span>
                    <span className="text-xs capitalize text-texto-tenue">{s.tipo}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {sectorElegido && (
            <p className="mt-2 text-sm text-exito">
              ✓ Sector seleccionado: <strong>{sectorElegido.nombre}</strong>
            </p>
          )}
        </div>
      )}

      {modo === 'mapa' && (
        <Suspense fallback={<Espera etiqueta="Cargando el mapa…" />}>
          <MapaSelector
            sectores={sectores}
            puntoInicial={valor?.punto}
            onSeleccionar={(pin) => {
              const ajuste = ajustarOrigen(pin, sectores.map(aSectorGeo))
              onCambio(
                ajuste.metodo === 'sector'
                  ? { metodo: 'mapa', sectorId: ajuste.sectorId, celda: ajuste.celda, punto: ajuste.punto }
                  : { metodo: 'mapa', celda: ajuste.celda, punto: ajuste.punto },
              )
            }}
          />
        </Suspense>
      )}

      {modo === 'texto' && (
        <div>
          <Entrada
            value={textoLibre}
            onChange={(e) => {
              setTextoLibre(e.target.value)
              onCambio({ metodo: 'texto', sectorTexto: e.target.value })
            }}
            placeholder="Nombre del sector, barrio o urbanización"
          />
          {avisoDireccion && (
            <p className="mt-1.5 text-sm text-advertencia" role="alert">
              Por su privacidad, indique solo el nombre del sector — no la dirección exacta.
            </p>
          )}
          <MensajeAyuda>Se ubicará en el centro de su parroquia. Para mayor precisión, use la lista o el mapa.</MensajeAyuda>
        </div>
      )}
    </div>
  )
}
